
import React, { Suspense } from 'react';
import { Provider } from 'react-redux';
import { MemoryRouter, Routes, Route } from 'react-router-dom';

// Redux Store
import { reduxStore } from './store';

// Global Styles
import './App.css';
import './assets/styles/qms.css';
import './assets/styles/qmsreports.css';
import './assets/styles/dms.css';
import './assets/styles/material-icons.css';
import './assets/styles/style.css';
import './assets/styles/main.css';
import './assets/styles/qmsPrint.css';
import 'react-tabs/style/react-tabs.css';
// import "./assets/styles/ketcher.css";

// Shared Components
import Loading from './Loading';
import ErrorBoundary from './pages/ErrorBoundary';

/* ------------------------------------------------------------------ */
/*  Lazy Loaded Pages                                                  */
/* ------------------------------------------------------------------ */

// Core Pages
const Login = React.lazy(() => import('./pages/Login'));
const AccountAction = React.lazy(() => import('./pages/AccountAction'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const OAuthCallback = React.lazy(() => import('./pages/OAuthCallback'));
const PageNotFound = React.lazy(() => import('./pages/PageNotFound'));
const TokenValidator = React.lazy(() => import('./components/TokenValidator'));
// const TestData = React.lazy(() => import('./pages/TestData'));

/* ------------------------------ LEGO ------------------------------ */
const Grid = React.lazy(() => import('./pages/Lego/Grid'));
const ReportGrid = React.lazy(() => import('./pages/Lego/ReportGrid'));
const ViewHistory = React.lazy(() => import('./pages/History/ViewHistory'));
const TestTrendReport = React.lazy(() => import('./pages/Lego/TestTrendReport'));
const QMSConfigurableReport = React.lazy(() => import('./pages/Lego/QMSConfigurableReport'));
const DmsFolderGrid = React.lazy(() => import('./pages/Lego/DmsFolderGrid'));
const DmsFileGrid = React.lazy(() => import('./pages/Lego/DmsFileGrid'));
const DmsPrintQueueGrid = React.lazy(() => import('./pages/Lego/DmsPrintQueueGrid'));
const DmsControlCopyTracker = React.lazy(() => import('./pages/Lego/DmsFolderGrid'));

/* --------------------------- ADMINISTRATION ----------------------- */
// Site
const CreateSite = React.lazy(() => import('./pages/Adminstration/Site/CreateSite'));
const ViewSite = React.lazy(() => import('./pages/Adminstration/Site/ViewSite'));

// Settings
const SecuritySettings = React.lazy(() => import('./pages/Adminstration/Settings/SecuritySettings'));
const AppConfigurations = React.lazy(() => import('./pages/Adminstration/Settings/AppSettings'));

// Identity & Access
const CreateDmsDepartment = React.lazy(() => import('./pages/Adminstration/IdentityAccess/DmsDepartments/CreateDmsDepartments'));
const ViewDmsDepartment = React.lazy(() => import('./pages/Adminstration/IdentityAccess/DmsDepartments/ViewDmsDepartments'));
const CreateUser = React.lazy(() => import('./pages/Adminstration/IdentityAccess/User/CreateUser'));
const ViewUser = React.lazy(() => import('./pages/Adminstration/IdentityAccess/User/ViewUser'));
const CreateGroup = React.lazy(() => import('./pages/Adminstration/IdentityAccess/Group/CreateGroup'));
const ViewGroup = React.lazy(() => import('./pages/Adminstration/IdentityAccess/Group/ViewGroup'));
const CreateUserType = React.lazy(() => import('./pages/Adminstration/IdentityAccess/UserTypes/CreateUserType'));
const ViewUserType = React.lazy(() => import('./pages/Adminstration/IdentityAccess/UserTypes/ViewUserType'));

// Licenses
const License = React.lazy(() => import('./pages/Adminstration/Licenses/License'));

// Auxiliaries
const CreateRemark = React.lazy(() => import('./pages/Adminstration/Auxiliaries/DefaultRemark/CreateRemark'));
const ViewRemark = React.lazy(() => import('./pages/Adminstration/Auxiliaries/DefaultRemark/ViewRemark'));
const CreateUnit = React.lazy(() => import('./pages/Adminstration/Auxiliaries/UnitMeasurement/CreateUnit'));
const ViewUnit = React.lazy(() => import('./pages/Adminstration/Auxiliaries/UnitMeasurement/ViewUnit'));
const CreateReasonCode = React.lazy(() => import('./pages/Adminstration/Auxiliaries/ReasonCode/CreateReasonCode'));
const ViewReasonCode = React.lazy(() => import('./pages/Adminstration/Auxiliaries/ReasonCode/ViewReasonCode'));

// Signature / Approval / Email
const SignatureConfiguration = React.lazy(() => import('./pages/Adminstration/Signature/SignatureConfiguration'));
const CreateApproval = React.lazy(() => import('./pages/Adminstration/Approval/Create'));
const ViewApproval = React.lazy(() => import('./pages/Adminstration/Approval/View'));
const CreateEmail = React.lazy(() => import('./pages/Adminstration/Email/Create'));
const ViewEmail = React.lazy(() => import('./pages/Adminstration/Email/View'));

// File Server
const CreateFileServerConfiguration = React.lazy(() => import('./pages/Adminstration/FileServerConfiguration/Create'));
const ViewFileServerConfiguration = React.lazy(() => import('./pages/Adminstration/FileServerConfiguration/View'));

// Workflow
const Workflow = React.lazy(() => import('./pages/Adminstration/workflow/ViewWorkflow'));
const DraggableItems = React.lazy(() => import('./pages/Adminstration/workflow/Create'));

// Accreditation / ID Generation
const CreateAccredationNumber = React.lazy(() => import('./pages/Adminstration/AccredationNumber/Create'));
const ViewAccredationNumber = React.lazy(() => import('./pages/Adminstration/AccredationNumber/View'));
const CreateIdGeneration = React.lazy(() => import('./pages/Adminstration/Idgeneration/Create'));
const ViewIdGeneration = React.lazy(() => import('./pages/Adminstration/Idgeneration/View'));
// const CreateQmsIdGeneration = React.lazy(() => import("./pages/Adminstration/Idgeneration1/Create"));
// const ViewQmsIdGeneration = React.lazy(() => import("./pages/Adminstration/Idgeneration1/View"));

// Configuration / API Mapping / Due Date / DMS Workflow
const CreateConfiguration = React.lazy(() => import('./pages/Adminstration/configuration/Create'));
const ViewConfiguration = React.lazy(() => import('./pages/Adminstration/configuration/View'));
const CreateApiMapping = React.lazy(() => import('./pages/Adminstration/ApiMapping/Create'));
const ViewApiMapping = React.lazy(() => import('./pages/Adminstration/ApiMapping/View'));
const CreateDueDateConfiguration = React.lazy(() => import('./pages/Adminstration/DueDateConfiguration/CreateDueDateConfiguration'));
const ViewDueDateConfiguration = React.lazy(() => import('./pages/Adminstration/DueDateConfiguration/ViewDueDateConfiguration'));
const CreateDmsWorkflow = React.lazy(() => import('./pages/Adminstration/DmsWorkFlowConfiguration/Create'));
const ViewDmsWorkflow = React.lazy(() => import('./pages/Adminstration/DmsWorkFlowConfiguration/View'));

/* ------------------------------ MASTERS --------------------------- */
// Grouping
const CreateGrouping = React.lazy(() => import('./pages/Masters/Grouping/Create'));
const ViewGrouping = React.lazy(() => import('./pages/Masters/Grouping/View'));

// Document Template
const CreateTemplateCreation = React.lazy(() => import('./pages/Masters/DocumentTemplate/TemplateCreation/Create'));
const ViewTemplateCreation = React.lazy(() => import('./pages/Masters/DocumentTemplate/TemplateCreation/View'));
const CreateCoverPage = React.lazy(() => import('./pages/Masters/DocumentTemplate/CoverPageCreation/Create'));
const ViewCoverPage = React.lazy(() => import('./pages/Masters/DocumentTemplate/CoverPageCreation/View'));

// Building Blocks
const CreateSharedList = React.lazy(() => import('./pages/Masters/BuildingBlocks/SharedList/CreateSharedList'));
const ViewSharedList = React.lazy(() => import('./pages/Masters/BuildingBlocks/SharedList/ViewSharedList'));
const CreateCustomFields = React.lazy(() => import('./pages/Masters/BuildingBlocks/CustomFields/CreateCustomFields'));
const CustomFieldsTextBox = React.lazy(() => import('./pages/Masters/BuildingBlocks/CustomFields/CustomFieldTypes/CustomFieldsTextBox'));
const ViewCustomFields = React.lazy(() => import('./pages/Masters/BuildingBlocks/CustomFields/ViewCustomFields'));

// Storage
const CreateCondition = React.lazy(() => import('./pages/Masters/Storage/Condition/CreateCondition'));
const ViewCondition = React.lazy(() => import('./pages/Masters/Storage/Condition/ViewCondition'));
const CreateBuilding = React.lazy(() => import('./pages/Masters/Storage/Building/CreateBuilding'));
const CreateDepartment = React.lazy(() => import('./pages/Masters/Storage/Department/CreateDepartment'));
const CreateRoom = React.lazy(() => import('./pages/Masters/Storage/Room/CreateRoom'));
const CreateLocation = React.lazy(() => import('./pages/Masters/Storage/Location/CreateLocation'));
const ViewBuilding = React.lazy(() => import('./pages/Masters/Storage/Building/ViewBuilding'));
const ViewRoom = React.lazy(() => import('./pages/Masters/Storage/Room/ViewRoom'));
const ViewDepartment = React.lazy(() => import('./pages/Masters/Storage/Department/ViewDepartment'));
const ViewLocation = React.lazy(() => import('./pages/Masters/Storage/Location/ViewLocation'));
const CreateDocumentStorage = React.lazy(() => import('./pages/Masters/Storage/DocumentStorage/CreateDocumentStorage'));
const ViewDocumentStorage = React.lazy(() => import('./pages/Masters/Storage/DocumentStorage/ViewDocumentStorage'));

// Document Type

const CreateDmsDocumentType = React.lazy(() => import('./pages/Masters/DocumentType/CreateDocumentType'));
const ViewDmsDocumentType = React.lazy(() => import('./pages/Masters/DocumentType/ViewDocumentType'));
const CreateQmsDepartment = React.lazy(() => import('./pages/Masters/Department/Create'));
const ViewQmsDepartment = React.lazy(() => import('./pages/Masters/Department/View'));
/* -------------------------------- LDMS ---------------------------- */
const CreateDocumentType = React.lazy(() => import('./pages/LDMS/DocumentType/CreateDocumentType'));
const ViewDocumentType = React.lazy(() => import('./pages/LDMS/DocumentType/ViewDocumentType'));
const CreateDocumentUpload = React.lazy(() => import('./pages/LDMS/DocumentUpload/CreateDocumentUpload'));
const ViewDocumentUpload = React.lazy(() => import('./pages/LDMS/DocumentUpload/ViewDocumentUpload'));
const CreateDocumentMapping = React.lazy(() => import('./pages/LDMS/DocumentMapping/CreateDocumentMapping'));
const ViewDocumentMapping = React.lazy(() => import('./pages/LDMS/DocumentMapping/ViewDocumentMapping'));
const CreateDocumentArchival = React.lazy(() => import('./pages/LDMS/DocumentArchival/CreateDocumentArchival'));
const ViewDocumentArchival = React.lazy(() => import('./pages/LDMS/DocumentArchival/ViewDocumentArchival'));

/* ---------------------- DMS DOCUMENT MANAGEMENT ------------------- */
// Document Initiation
const CreateDocumentInitiation = React.lazy(() => import('./pages/DocumentManagement/DocumentInitiation/Create'));
const ViewDocumentInitiation = React.lazy(() => import('./pages/DocumentManagement/DocumentInitiation/View'));

// Document Creation
const CreateDocumentCreation = React.lazy(() => import('./pages/DocumentManagement/DocumentCreation/Create'));
const ViewDocumentCreation = React.lazy(() => import('./pages/DocumentManagement/DocumentCreation/View'));

// Draft Review
const ViewDocumentDraftReview = React.lazy(() => import('./pages/DocumentManagement/DocumentDraftReview/ViewDocumentDraftReview'));
const DocumentDraftReview = React.lazy(() => import('./pages/DocumentManagement/DocumentDraftReview/DraftApprovalReview'));

// Approval
const ViewDocumentApproval = React.lazy(() => import('./pages/DocumentManagement/DocumentApproval/ViewDocumentApproval'));
const DocumentApprovalReview = React.lazy(() => import('./pages/DocumentManagement/DocumentApproval/ApprovalReview'));

// Effective / Training / Request / Issuance / Retrieval
const CreateDocumentForEffective = React.lazy(() => import('./pages/DocumentManagement/DocumentForEffective/Create'));
const CreateDocumentTrainingRecord = React.lazy(() => import('./pages/DocumentManagement/DocumentTrainingRecord/Create'));
const ViewDocumentTrainingRecord = React.lazy(() => import('./pages/DocumentManagement/DocumentTrainingRecord/View'));
const CreateDocumentRequest = React.lazy(() => import('./pages/DocumentManagement/DocumentRequest/Create'));
const ViewDocumentRequest = React.lazy(() => import('./pages/DocumentManagement/DocumentRequest/View'));
const CreateDocumentIssuance = React.lazy(() => import('./pages/DocumentManagement/DocumentIssuance/Create'));
const ViewDocumentIssuance = React.lazy(() => import('./pages/DocumentManagement/DocumentIssuance/View'));
const CreateDocumentRetrieval = React.lazy(() => import('./pages/DocumentManagement/DocumentRetrieval/Create'));
const ViewDocumentRetrieval = React.lazy(() => import('./pages/DocumentManagement/DocumentRetrieval/View'));

// Obsolete / Revision / My Documents
const CreateDocumentObsolete = React.lazy(() => import('./pages/DocumentManagement/DocumentObsolete/Create'));
const ViewDocumentObsolete = React.lazy(() => import('./pages/DocumentManagement/DocumentObsolete/View'));
const UnderRevision = React.lazy(() => import('./pages/DocumentManagement/DocumentRevision/UnderRevision/Create'));
const RevisedDocuments = React.lazy(() => import('./pages/DocumentManagement/DocumentRevision/RevisedDocuments/View'));
const ViewMyDocuments = React.lazy(() => import('./pages/DocumentManagement/MyDocuments/ViewMyDocuments'));
const ViewControlCopies = React.lazy(() => import('./pages/DocumentManagement/DocumentLoaction/View'));
const DocumentArchival = React.lazy(() => import('./pages/DocumentManagement/DocumentArchival/Archival'));

// Reports
const DocumentSuperseded = React.lazy(() => import('./pages/DocumentManagement/Reports/SupersededDocuments/View'));
const DocumentTrackReport = React.lazy(() => import('./pages/DocumentManagement/Reports/DocumentTrackReport'));
const DocumentMatrixReport = React.lazy(() => import('./pages/DocumentManagement/Reports/DocumentMatrixReport'));
const VersionComparisonReport = React.lazy(() => import('./pages/DocumentManagement/Reports/VersionComparisonReport'));

/* ------------------------------ FORMS ----------------------------- */
const CreateFormType = React.lazy(() => import('./pages/Forms/FormType/Create'));
const ViewFormType = React.lazy(() => import('./pages/Forms/FormType/View'));
const CreateFormRequest = React.lazy(() => import('./pages/Forms/FormRequest/Create'));
const ViewFormRequest = React.lazy(() => import('./pages/Forms/FormRequest/View'));
const CreateIssueForm = React.lazy(() => import('./pages/Forms/IssueForm/Create'));
const ViewIssueForm = React.lazy(() => import('./pages/Forms/IssueForm/View'));
const CreateFormFilling = React.lazy(() => import('./pages/Forms/FormFilling/Create'));
const ViewFormFilling = React.lazy(() => import('./pages/Forms/FormFilling/View'));
const ViewFormApproval = React.lazy(() => import('./pages/Forms/FormApproval/ViewFormApproval'));
const CreateFormRetrieval = React.lazy(() => import('./pages/Forms/FormRetrieval/Create'));
const ViewFormRetrieval = React.lazy(() => import('./pages/Forms/FormRetrieval/View'));

/* ------------------------- PRINT MANAGEMENT ----------------------- */
const CreateAgentConfiguration = React.lazy(() => import('./pages/PrintManagement/AgentConfiguration/Create'));
const ViewAgentConfiguration = React.lazy(() => import('./pages/PrintManagement/AgentConfiguration/View'));
const CreatePrinterConfiguration = React.lazy(() => import('./pages/PrintManagement/PrinterConfiguration/Create'));
const ViewPrinterConfiguration = React.lazy(() => import('./pages/PrintManagement/PrinterConfiguration/View'));
const CreatePrintRequest = React.lazy(() => import('./pages/PrintManagement/PrintRequest/Create'));
const ViewPrintRequest = React.lazy(() => import('./pages/PrintManagement/PrintRequest/View'));
const ViewPrintActions = React.lazy(() => import('./pages/PrintManagement/Print/PrintActions'));
const CreateRePrintRequest = React.lazy(() => import('./pages/PrintManagement/RePrintRequest/Create'));
const ViewRePrintRequest = React.lazy(() => import('./pages/PrintManagement/RePrintRequest/View'));
const ViewRePrintActions = React.lazy(() => import('./pages/PrintManagement/RePrint/RePrintActions'));
const CreatePrintIssuance = React.lazy(() => import('./pages/PrintManagement/PrintIssuance/Create'));
const ViewPrintIssuance = React.lazy(() => import('./pages/PrintManagement/PrintIssuance/View'));
const CreatePrintDestruction = React.lazy(() => import('./pages/PrintManagement/PrintDestruction/Create'));
const ViewPrintDestruction = React.lazy(() => import('./pages/PrintManagement/PrintDestruction/View'));

/* ------------------------------------------------------------------ */
/*  App Component                                                      */
/* ------------------------------------------------------------------ */

function App() {
  return (
    <MemoryRouter>
      <ErrorBoundary>
        <Provider store={reduxStore}>
          <Suspense fallback={<Loading />}>
            <TokenValidator />
            <Routes>

              {/* ---------------------- Core Routes ---------------------- */}
              <Route path="/" element={<Login />} />
              <Route path="/login" element={<Login />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/oauth2/callback" element={<OAuthCallback />} />
              <Route path="/account-action" element={<AccountAction />} />
              <Route path="/logout" element={<Login />} />

              {/* ------------------------ LEGO --------------------------- */}
              <Route path="/lego/:module_name/:resource_path/:menu_id?" element={<Grid />} />
              <Route path="/lego-report-grid/:module_name/:resource_path/:menu_id?" element={<ReportGrid />} />
              <Route path="/lego-test-trend-report-grid/:menu_id/:resource_path/:menu_id?" element={<TestTrendReport />} />
              <Route path="/v-history/:id/:resource_path/:menu_id/:is_print?" element={<ViewHistory />} />
              {/* <Route path="/test-data" element={<TestData />} /> */}
              <Route path="/qms-lego-report-grid/:module_name/:resource_path" element={<QMSConfigurableReport />} />
              <Route path="/dms-folder-grid/:module_name/:resource_path/:menu_id?" element={<DmsFolderGrid />} />
              <Route path="/dms-file-grid/:module_name/:resource_path/:menu_id?" element={<DmsFileGrid />} />
              <Route path="/dms-print-queue-grid/:module_name/:resource_path/:menu_id?" element={<DmsPrintQueueGrid />} />
              <Route path="/dms-control-copy-tracker/:module_name/:resource_path/:menu_id?" element={<DmsControlCopyTracker />} />

              {/* -------------------- ADMINISTRATION --------------------- */}
              {/* Site */}
              <Route path="/c-sites/:id/:ref/:action?" element={<CreateSite />} />
              <Route path="/v-sites/:id/:ref/:action?" element={<ViewSite />} />

              {/* Settings */}
              <Route path="/security-settings" element={<SecuritySettings />} />
              <Route path="/security-settings/:id/:ref/:action?" element={<SecuritySettings />} />
              <Route path="/app-configs" element={<AppConfigurations />} />

              {/* Identity & Access */}
              <Route path="/c-departments/:id/:ref/:action?" element={<CreateDmsDepartment />} />
              <Route path="/v-departments/:id/:ref/:action?" element={<ViewDmsDepartment />} />
              <Route path="/c-users/:id/:ref/:action?" element={<CreateUser />} />
              <Route path="/v-users/:id/:ref/:action?" element={<ViewUser />} />
              <Route path="/c-groups/:id/:ref/:action?" element={<CreateGroup />} />
              <Route path="/v-groups/:id/:ref/:action?" element={<ViewGroup />} />
              <Route path="/c-user-types/:id/:ref/:action?" element={<CreateUserType />} />
              <Route path="/v-user-types/:id/:ref/:action?" element={<ViewUserType />} />

              {/* License */}
              <Route path="/license" element={<License />} />

              {/* Auxiliaries */}
              <Route path="/c-default-reasons/:id/:ref/:action?" element={<CreateRemark />} />
              <Route path="/v-default-reasons/:id/:ref/:action?" element={<ViewRemark />} />
              <Route path="/c-uom/:id/:ref/:action?" element={<CreateUnit />} />
              <Route path="/v-uom/:id/:ref/:action?" element={<ViewUnit />} />
              <Route path="/c-reason-code/:id/:ref/:action?" element={<CreateReasonCode />} />
              <Route path="/v-reason-code/:id/:ref/:action?" element={<ViewReasonCode />} />

              {/* Signature / Approval / Email */}
              <Route path="/e-signature" element={<SignatureConfiguration />} />
              <Route path="/c-approval-config/:id/:ref/:action?" element={<CreateApproval />} />
              <Route path="/v-approval-config/:id/:ref/:action?" element={<ViewApproval />} />
              <Route path="/c-e-mail/:id/:ref/:action?" element={<CreateEmail />} />
              <Route path="/v-e-mail/:id/:ref/:action?" element={<ViewEmail />} />

              {/* File Server */}
              <Route path="/c-fileserver-config/:id/:ref/:action?" element={<CreateFileServerConfiguration />} />
              <Route path="/v-fileserver-config/:id/:ref/:action?" element={<ViewFileServerConfiguration />} />

              {/* Workflow */}
              <Route path="/v-workflow-configuration/:id/:ref/:action?" element={<Workflow />} />
              <Route path="/c-workflow-configuration/:id/:ref/:action?" element={<DraggableItems />} />

              {/* Accreditation / ID Generation */}
              <Route path="/c-accredation-number/:id/:ref/:action?" element={<CreateAccredationNumber />} />
              <Route path="/v-accredation-number/:id/:ref/:action?" element={<ViewAccredationNumber />} />
              <Route path="/c-id-generation/:id/:ref/:action?" element={<CreateIdGeneration />} />
              <Route path="/v-id-generation/:id/:ref/:action?" element={<ViewIdGeneration />} />

              {/* Configuration / API Mapping / Due Date / DMS Workflow */}
              <Route path="/c-due-date-configuration/:id/:ref/:action?" element={<CreateDueDateConfiguration />} />
              <Route path="/v-due-date-configuration/:id/:ref/:action?" element={<ViewDueDateConfiguration />} />
              <Route path="/c-configuration/:id/:ref/:action?" element={<CreateConfiguration />} />
              <Route path="/v-configuration/:id/:ref/:action?" element={<ViewConfiguration />} />
              <Route path="/c-api-mapping/:id/:ref/:action?" element={<CreateApiMapping />} />
              <Route path="/v-api-mapping/:id/:ref/:action?" element={<ViewApiMapping />} />
              <Route path="/c-dms-workflow-configuration/:id/:ref/:action?" element={<CreateDmsWorkflow />} />
              <Route path="/v-dms-workflow-configuration/:id/:ref/:action?" element={<ViewDmsWorkflow />} />

              {/* ------------------------- MASTERS ----------------------- */}
              {/* Grouping */}
              <Route path="/c-grouping/:id/:ref/:action?" element={<CreateGrouping />} />
              <Route path="/v-grouping/:id/:ref/:action?" element={<ViewGrouping />} />

              {/* Building Blocks */}
              <Route path="/c-shared-list/:id/:ref/:action?" element={<CreateSharedList />} />
              <Route path="/v-shared-list/:id/:ref/:action?" element={<ViewSharedList />} />
              <Route path="/c-custom-fields/:id/:ref/:action?" element={<CreateCustomFields />} />
              <Route path="/v-custom-fields/:id/:ref/:action?" element={<ViewCustomFields />} />
              <Route path="/create-custom-text-fields/:id/:ref/:action?" element={<CustomFieldsTextBox />} />

              {/* Document Template */}
              <Route path="/c-template-creation/:id/:ref/:action?" element={<CreateTemplateCreation />} />
              <Route path="/v-template-creation/:id/:ref/:action?" element={<ViewTemplateCreation />} />
              <Route path="/c-cover-page-creation/:id/:ref/:action?" element={<CreateCoverPage />} />
              <Route path="/v-cover-page-creation/:id/:ref/:action?" element={<ViewCoverPage />} />

              {/* Storage */}
              <Route path="/c-storage-condition/:id/:ref/:action?" element={<CreateCondition />} />
              <Route path="/v-storage-condition/:id/:ref/:action?" element={<ViewCondition />} />
              <Route path="/c-storage-buildings/:id/:ref/:action?" element={<CreateBuilding />} />
              <Route path="/c-storage-department/:id/:ref/:action?" element={<CreateDepartment />} />
              <Route path="/c-storage-room/:id/:ref/:action?" element={<CreateRoom />} />
              <Route path="/c-storage-location/:id/:ref/:action?" element={<CreateLocation />} />
              <Route path="/c-document-storage/:id/:ref/:action?" element={<CreateDocumentStorage />} />
              <Route path="/v-storage-buildings/:id/:ref/:action?" element={<ViewBuilding />} />
              <Route path="/v-storage-Location/:id/:ref/:action?" element={<ViewLocation />} />
              <Route path="/v-storage-department/:id/:ref/:action?" element={<ViewDepartment />} />
              <Route path="/v-storage-room/:id/:ref/:action?" element={<ViewRoom />} />
              <Route path="/v-document-storage/:id/:ref/:action?" element={<ViewDocumentStorage />} />

              {/* Document Type */}
              
              <Route path="/c-document-types/:id/:ref/:action?" element={<CreateDmsDocumentType />} />
              <Route path="/v-document-types/:id/:ref/:action?" element={<ViewDmsDocumentType />} />
                    {/* Department / Document Type */}
              <Route path="/c-department/:id/:ref/:action?" element={<CreateQmsDepartment />} />
              <Route path="/v-department/:id/:ref/:action?" element={<ViewQmsDepartment />} />

              {/* -------------------------- LDMS ------------------------- */}
              <Route path="/c-ldms-document-types/:id/:ref/:action?" element={<CreateDocumentType />} />
              <Route path="/v-ldms-document-types/:id/:ref/:action?" element={<ViewDocumentType />} />
              <Route path="/c-ldms-document-uploads/:id/:ref/:action?" element={<CreateDocumentUpload />} />
              <Route path="/v-ldms-document-uploads/:id/:ref/:action?" element={<ViewDocumentUpload />} />
              <Route path="/c-ldms-document-module-mapping/:id/:ref/:action?" element={<CreateDocumentMapping />} />
              <Route path="/v-ldms-document-module-mapping/:id/:ref/:action?" element={<ViewDocumentMapping />} />
              <Route path="/c-ldms-document-archival/:id/:ref/:action?" element={<CreateDocumentArchival />} />
              <Route path="/v-ldms-document-archival/:id/:ref/:action?" element={<ViewDocumentArchival />} />

              {/* ---------------- DMS DOCUMENT MANAGEMENT ---------------- */}
              {/* Initiation */}
              <Route path="/c-document-initiation/:id/:ref/:action?" element={<CreateDocumentInitiation />} />
              <Route path="/v-document-initiation/:id/:ref/:action?" element={<ViewDocumentInitiation />} />

              {/* Creation */}
              <Route path="/c-document-creation/:id/:ref/:action?" element={<CreateDocumentCreation />} />
              <Route path="/v-document-creation/:id/:ref/:action?" element={<ViewDocumentCreation />} />

              {/* Draft Review */}
              <Route path="/v-document-draft-review/:id/:ref/:action?" element={<ViewDocumentDraftReview />} />
              <Route path="/v-document-draft-review-approval/:id/:ref/:action?" element={<DocumentDraftReview />} />

              {/* Approval */}
              <Route path="/v-document-approval/:id/:ref/:action?" element={<ViewDocumentApproval />} />
              <Route path="/v-document-review-approval/:id/:ref/:action?" element={<DocumentApprovalReview />} />

              {/* Effective / Training / Request / Issuance / Retrieval */}
              <Route path="/v-document-for-effective/:id/:ref/:action?" element={<CreateDocumentForEffective />} />
              <Route path="/c-document-training-record/:id/:ref/:action?" element={<CreateDocumentTrainingRecord />} />
              <Route path="/v-document-training-record/:id/:ref/:action?" element={<ViewDocumentTrainingRecord />} />
              <Route path="/c-document-request/:id/:ref/:action?" element={<CreateDocumentRequest />} />
              <Route path="/v-document-request/:id/:ref/:action?" element={<ViewDocumentRequest />} />
              <Route path="/c-document-issuance/:id/:ref/:action?" element={<CreateDocumentIssuance />} />
              <Route path="/v-document-issuance/:id/:ref/:action?" element={<ViewDocumentIssuance />} />
              <Route path="/c-document-retrieval/:id/:ref/:action?" element={<CreateDocumentRetrieval />} />
              <Route path="/v-document-retrieval/:id/:ref/:action?" element={<ViewDocumentRetrieval />} />

              {/* Revision / Obsolete / My Documents */}
              <Route path="/v-document-under-revision/:id/:ref/:action?" element={<UnderRevision />} />
              <Route path="/v-document-revised-documents/:id/:ref/:action?" element={<RevisedDocuments />} />
              <Route path="/c-document-obsolete/:id/:ref/:action?" element={<CreateDocumentObsolete />} />
              <Route path="/v-document-obsolete/:id/:ref/:action?" element={<ViewDocumentObsolete />} />
              <Route path="/v-document-my-documents/:id/:ref/:action?" element={<ViewMyDocuments />} />
              <Route path="/v-document-location/:id/:ref/:action?" element={<ViewControlCopies />} />
              <Route path="/v-document-archival/:id/:ref/:action?" element={<DocumentArchival />} />

              {/* Reports */}
              <Route path="/v-superseded-documents/:id/:ref/:action?" element={<DocumentSuperseded />} />
              <Route path="/lego/document-document-track-report" element={<DocumentTrackReport />} />
              <Route path="/lego/document-document-track-report/:resource_path/:menu_id?" element={<DocumentTrackReport />} />
              <Route path="/lego/document-document-matrix-report" element={<DocumentMatrixReport />} />
              <Route path="/lego/document-document-matrix-report/:resource_path/:menu_id?" element={<DocumentMatrixReport />} />
              <Route path="/v-document-version-comparison-report/:id/:ref/:action?" element={<VersionComparisonReport />} />

              {/* ------------------------- DMS FORMS --------------------- */}
              <Route path="/c-form-type/:id/:ref/:action?" element={<CreateFormType />} />
              <Route path="/v-form-type/:id/:ref/:action?" element={<ViewFormType />} />
              <Route path="/c-form-request/:id/:ref/:action?" element={<CreateFormRequest />} />
              <Route path="/v-form-request/:id/:ref/:action?" element={<ViewFormRequest />} />
              <Route path="/c-issue-form/:id/:ref/:action?" element={<CreateIssueForm />} />
              <Route path="/v-issue-form/:id/:ref/:action?" element={<ViewIssueForm />} />
              <Route path="/c-form-filling/:id/:ref/:action?" element={<CreateFormFilling />} />
              <Route path="/v-form-filling/:id/:ref/:action?" element={<ViewFormFilling />} />
              <Route path="/v-form-approval/:id/:ref/:action" element={<ViewFormApproval />} />
              <Route path="/c-form-retrieval/:id/:ref/:action?" element={<CreateFormRetrieval />} />
              <Route path="/v-form-retrieval/:id/:ref/:action?" element={<ViewFormRetrieval />} />

              {/* ------------------- DMS PRINT MANAGEMENT ---------------- */}
              <Route path="/c-agent-configuration/:id/:ref/:action?" element={<CreateAgentConfiguration />} />
              <Route path="/v-agent-configuration/:id/:ref/:action?" element={<ViewAgentConfiguration />} />
              <Route path="/c-printer-configuration/:id/:ref/:action?" element={<CreatePrinterConfiguration />} />
              <Route path="/v-printer-configuration/:id/:ref/:action?" element={<ViewPrinterConfiguration />} />
              <Route path="/c-print-request/:id/:ref/:action?" element={<CreatePrintRequest />} />
              <Route path="/v-print-request/:id/:ref/:action?" element={<ViewPrintRequest />} />
              <Route path="/v-print/:id/:ref/:action?" element={<ViewPrintActions />} />
              <Route path="/c-reprint-request/:id/:ref/:action?" element={<CreateRePrintRequest />} />
              <Route path="/v-reprint-request/:id/:ref/:action?" element={<ViewRePrintRequest />} />
              <Route path="/v-reprint/:id/:ref/:action?" element={<ViewRePrintActions />} />
              <Route path="/c-print-issuance/:id/:ref/:action?" element={<CreatePrintIssuance />} />
              <Route path="/v-print-issuance/:id/:ref/:action?" element={<ViewPrintIssuance />} />
              <Route path="/c-print-destruction/:id/:ref/:action?" element={<CreatePrintDestruction />} />
              <Route path="/v-print-destruction/:id/:ref/:action?" element={<ViewPrintDestruction />} />

              {/* -------------------- Fallback Route --------------------- */}
              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </Suspense>
        </Provider>
      </ErrorBoundary>
    </MemoryRouter>
  );
}

export default App;